import * as Sentry from '@sentry/browser';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { FAILURE, STARTING, STREAM, SUCCESS } from './util/action-name-creators';
import { RequestState, shouldStatusExecute } from '../reducers/utils/state-manager';
import { createAction } from 'redux-actions';

import { openOrCloseGlobalDialog } from './dialog/global-dialog';

interface ActionDispatchOptions<T> {
  /**
   * The name of the action that will be fired for redux. It will use the starting, success, and failure functions in `action-name-creators` to
   * dispatch the right action during the right phase of loading
   */
  name: string;
  /**
   * The function that we should use to load data from. Typically, this will be a function from `api.js`
   */
  loadFunction: () => Promise<Response>;
  /**
   * The args that were passed into the parent function, which are passed through to the redux reducer. This is important in knowing what to update in
   * state very frequently, so best practice is to include things that you might not even use
   */
  args: { [arg: string]: any };
  /**
   * The first of three `shouldExecute` options. These functions control whether we'll retry a GET request, and should almost never be included for POST requests.
   * This is the simplest of the three, and just gives you all the data the dispatcher has and asks you to return true if we should make the request and false otherwise.
   */
  shouldExecute?: (state: any, args: { [arg: string]: any }) => boolean;
  /**
   * The second, and most common, of three `shouldExecute` options. These functions control whether we'll retry a GET request, and should almost never be included for POST requests.
   * This is a selector from reselect that we can use to run `shouldStatusExecute` to see if we should reload data (namely, if it's dirty or we've not attempted)
   */
  shouldExecuteSelector?: Function;
  /**
   * The last of three `shouldExecute` options. These functions control whether we'll retry a GET request, and should almost never be included for POST requests.
   * This is an array of selectors that we use to check `shouldStatusExecute` on. If any of the status objects require reloading, we'll run the fetch. The usage of this option
   * is for time when a single entity can be loaded by various query params (jobs have orgId and jobId, for example).
   */
  shouldExecuteSelectors?: (state: any) => Array<RequestState | null>;
  /**
   * A function that is called after the success action has been dispatched. This is useful for having post-request behaviors like showing notifications or setting other parts
   * of state as dirty.
   */
  onSuccess?: (
    dispatch: ThunkDispatch<any, any, AnyAction>,
    getState: () => any,
    data: T,
  ) => Promise<void> | void;
  /**
   * A function that is called when we specifically hit a `404` response from the server. This is useful in enabling "Not Found" page capabilities in a variety of situations
   */
  notFoundHandler?: (args: { [arg: string]: any }) => void;

  /**
   * If provided, dispatches the action as a stream. Which is to say, emits <name>_STREAM events on each chunk received with the data parsed by parseStreamChunks.
   */
  parseStreamChunk?: (value: Uint8Array | undefined) => object[];
}

type DispatchFunction<T> = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  getState: () => any,
) => Promise<T>;

// eslint is freaking out about this generic definition
// eslint-disable-next-line arrow-parens
export const createGenericDispatcher = <T = any>(
  config: ActionDispatchOptions<T>,
): DispatchFunction<T> => {
  const LOAD_STARTING = STARTING(config.name);
  const LOAD_SUCCESS = SUCCESS(config.name);
  const LOAD_STREAM = STREAM(config.name);
  const LOAD_FAILURE = FAILURE(config.name);

  const loadStart = createAction(LOAD_STARTING);
  const loadSuccess = createAction(LOAD_SUCCESS);
  const loadStream = createAction(LOAD_STREAM);
  const loadFailure = createAction(LOAD_FAILURE);

  return async (dispatch: ThunkDispatch<any, any, AnyAction>, getState: () => any) => {
    const { shouldExecute, shouldExecuteSelector, shouldExecuteSelectors } = config;
    if (shouldExecuteSelectors) {
      const state = getState();
      if (shouldExecuteSelectors(state).every((s) => !shouldStatusExecute(s))) {
        return null;
      }
    } else if (shouldExecuteSelector) {
      const state = getState();
      if (!shouldStatusExecute(shouldExecuteSelector(state))) {
        return null;
      }
    } else if (shouldExecute) {
      const state = getState();
      if (!shouldExecute(state, config.args)) {
        return null;
      }
    }

    dispatch(loadStart({ args: config.args }));
    let response: Response | null = null;
    try {
      response = await config.loadFunction();
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('actionName', config.name);
        scope.setExtra('raw_error', e);
        scope.setExtra('args', config.args);

        Sentry.captureException(e);
      });
    }
    if (response && response.status === 404 && config.notFoundHandler) {
      config.notFoundHandler(config.args);
      dispatch(loadFailure({ args: config.args }));
      return { error: new Error('Not Found') };
    }
    if (!response || !response.ok) {
      let errorMessage;
      try {
        errorMessage = response ? await response.json() : null;
      } catch (e) {
        console.error(e); // eslint-disable-line
      }
      const error = new Error(
        errorMessage && errorMessage.message ? errorMessage.message : 'API Request Failed',
      );
      // Didn't import the key from global-dialogs-config.js here so that there isn't a cyclical dependency
      dispatch(openOrCloseGlobalDialog('globalError', true));
      dispatch(loadFailure({ args: config.args, error }));
      return { error };
    }
    let data = null;

    const { parseStreamChunk } = config;
    try {
      if (parseStreamChunk) {
        // Consume response as stream
        const reader = response.body?.getReader();
        await reader?.read().then(async function processStream({ done, value }) {
          parseStreamChunk(value).forEach((datum) => {
            dispatch(
              loadStream({
                args: config.args,
                data: datum,
              }),
            );
          });

          if (!done) {
            await reader?.read().then(processStream);
          }
        });
      } else {
        // If the response is not in JSON format like from a route that uses sendStatus,
        // this will throw an error
        data = await response.json();
      }
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('actionName', config.name);
        scope.setExtra('raw_error', e);
        scope.setExtra('args', config.args);

        const message = parseStreamChunk
          ? 'Encountered error while reading stream'
          : 'Response from action does not return JSON';
        Sentry.captureMessage(message);
      });
      dispatch(loadFailure({ args: config.args }));
      return { error: new Error('Invalid Response') };
    }

    dispatch(
      loadSuccess({
        args: config.args,
        data,
      }),
    );
    if (config.onSuccess) {
      await config.onSuccess(dispatch, getState, data);
    }
    return data;
  };
};
